import {
  type Block, type Blocks, type BlockTable, type BlockTD, type BlockTR, FormatFlag, type ReviewTableCell, type ReviewTableRow,
  unwrapReviewTableCells, unwrapReviewTableRows,
} from './block'

/** Inline blocks a table cell holds (`InlineBlock` in the schema, plus a page link and an inline question). */
const CELL_INLINE: ReadonlySet<string> = new Set([
  'text', 'chars', 'br', 'code', 'a', 'em', 'l', 'mention', 'cloze', 'comment', 'criticComment', 'diff', 'page', 'pageLink',
])

export function isCellInline(block: Block): boolean {
  if (block.type === 'question') return block.inline === true
  if (block.type === 'review') return block.display === 'inline'
  return CELL_INLINE.has(block.type)
}

/**
 * A table cell's content at the inline level: inline blocks stay; any other block becomes its inline content, each
 * block's runs separated by a line break. A divider is a line break, a code block its lines as code, a block formula an
 * inline formula, media a link to its source (labelled by its caption, alt text or name), a page list its page cards,
 * a nested table its cells.
 */
export function cellInline(blocks: Blocks, nextId: () => string): Blocks {
  return flatten(blocks, nextId, false)
}

/** The table with every cell's content at the inline level. */
export function cellInlineTable(table: BlockTable, nextId: () => string): BlockTable {
  const td = (cell: BlockTD): BlockTD => ({ ...cell, children: cellInline(cell.children, nextId) })
  const tr = (row: BlockTR): BlockTR => ({ ...row, children: row.children.map((cell): BlockTD | ReviewTableCell =>
    cell.type === 'review' ? { ...cell, children: cell.children.map(td) } : td(cell)) })
  return { ...table, children: table.children.map((row): BlockTR | ReviewTableRow =>
    row.type === 'review' ? { ...row, children: row.children.map(tr) } : tr(row)) }
}

function flatten(blocks: Blocks, nextId: () => string, inLink: boolean): Blocks {
  const out: Blocks = []
  const lineBreak = () => {
    if (out.length > 0 && out.at(-1)?.type !== 'br') out.push({ id: nextId(), type: 'br' })
  }
  let separate = false
  for (const block of blocks) {
    if (block.type === 'hr') {
      if (out.at(-1)?.type !== 'br') out.push({ id: nextId(), type: 'br' })
      separate = false
      continue
    }
    const inline = inlineBlock(block, nextId, inLink)
    if (inline) {
      if (separate && block.type !== 'br') lineBreak()
      separate = false
      out.push(...inline)
      continue
    }
    const content = blockContent(block, nextId, inLink)
    if (content.length === 0) continue
    lineBreak()
    out.push(...content)
    separate = true
  }
  return out
}

/** An inline block, its children at the inline level, or the atom it reads as inline; undefined for block content. */
function inlineBlock(block: Block, nextId: () => string, inLink: boolean): Blocks | undefined {
  if (block.type === 'question') return block.inline === true ? [block] : undefined
  if (isCellInline(block)) {
    if (!('children' in block)) return [block]
    return [{ ...block, children: flatten(block.children as Blocks, nextId, inLink || block.type === 'a') } as Block]
  }
  if (block.type === 'img') return media(block.src, block.caption ?? (block.alt ? [chars(block.alt, nextId)] : []), nextId, inLink)
  if (block.type === 'video' || block.type === 'preview') return media(block.src, block.caption ?? [], nextId, inLink)
  if (block.type === 'audio') return media(block.src, [], nextId, inLink)
  if (block.type === 'file') return media(block.src, [chars(block.name, nextId)], nextId, inLink)
  if (block.type === 'pages') return block.ids.map((pageId) => ({ id: nextId(), type: 'page', pageId }))
  return undefined
}

// Inside a link, media is its label alone: a link cannot hold a link.
function media(src: string, label: Blocks, nextId: () => string, inLink: boolean): Blocks {
  const children = flatten(label, nextId, true)
  if (children.length === 0 && src === '') return []
  const text = children.length > 0 ? children : [chars(src, nextId)]
  return inLink ? text : [{ id: nextId(), type: 'a', href: src, children: text }]
}

/** A block-level block's inline content, its parts separated by line breaks. */
function blockContent(block: Block, nextId: () => string, inLink: boolean): Blocks {
  const join = (parts: Blocks[]) => flatten(parts.flatMap((part): Blocks => part.length === 0 ? [] :
    [{ id: nextId(), type: 'p', children: part }]), nextId, inLink)
  if (block.type === 'pre') {
    const text = plainText(block.children)
    if (block.latex) return [{ id: nextId(), type: 'code', latex: true, children: [chars(text, nextId)] }]
    const lines: Blocks = []
    text.split('\n').forEach((line, index) => {
      if (index > 0) lines.push({ id: nextId(), type: 'br' })
      if (line !== '') lines.push(chars(line, nextId, FormatFlag.CODE))
    })
    return lines
  }
  if (block.type === 'quote') return join([flatten(block.children, nextId, inLink), flatten(block.author ?? [], nextId, inLink)])
  if (block.type === 'alert') return join([block.title ? [chars(block.title, nextId)] : [], flatten(block.children, nextId, inLink)])
  if (block.type === 'table') return join(unwrapReviewTableRows(block.children).map((row) => {
    const line: Blocks = []
    for (const cell of unwrapReviewTableCells(row.children)) {
      const content = flatten(cell.children, nextId, inLink)
      if (content.length === 0) continue
      if (line.length > 0) line.push(chars(' ', nextId))
      line.push(...content)
    }
    return line
  }))
  if (block.type === 'question') return join([flatten(block.question ?? [], nextId, inLink),
    ...('opts' in block ? block.opts.map((option) => flatten(option, nextId, inLink)) : [])])
  if (block.type === 'pageTitle') return [chars(block.title, nextId)]
  return 'children' in block ? flatten(block.children as Blocks, nextId, inLink) : []
}

function chars(text: string, nextId: () => string, format?: number): Block {
  return { id: nextId(), type: 'chars', text: [{ id: nextId(), text }], ...(format ? { format } : {}) }
}

function plainText(blocks: Blocks): string {
  return blocks.map((block) => block.type === 'text' ? block.text
    : block.type === 'chars' ? block.text.map((part) => part.text).join('')
      : block.type === 'br' ? '\n'
        : 'children' in block ? plainText(block.children as Blocks) : '').join('')
}
